import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import {
    addWorkflowAdvanceService,
    getAccountApplicationService,
    getAccountDetailService,
    getAccountFuelOrderService,
    getAccountInquiriesService,
    getNotificationConfigRoleService,
    getNotificationConfigService,
    getUsersListService,
    registerUserService,
    searchUserAccountService,
    updateAccountStatusService,
    updateNotificationConfigService
} from '../../../services/adminServices';

import { NotificationConfigUpdate } from '../../types';
import { AccountsState, UpdateEmailArgs } from './types';
import { ListArgs, SearchTextArgs, UpdateAccountArgs, WorkflowAdvanceArgs } from 'MyAccountPlus-UIComponents';
import { SelfRegisterArgs } from '../../login-reducer/types';

const initialState: AccountsState = {
    successRegister: "",
    loading: false,
    error: "",
    success: "",
    accounts: {
        content: [],
        empty: false,
        totalPages: 0,
        totalElements: 0,
        numberOfElements: 0,
        pageNumber: 0
    },
    accountDetails: {
        account_status: '',
        comment: null,
        account_id: '',
        balance_30_days: '',
        balance_60_days: '',
        balance_90_days: '',
        balance_over_90_days: '',
        billing_address: {
            street1: '',
            city: '',
            state: '',
            zipcode: '',
            country: ''
        },
        budget_balance: '',
        budget_paid_YTD: '',
        budget_payment: '',
        display_name: '',
        email: '',
        first_name: '',
        home_phone: '',
        work_phone: '',
        cell_phone: '',
        is_budget_plan: false,
        is_cancelled: false,
        is_deleted: false,
        last_name: '',
        last_payment_amount: '',
        last_payment_date: '',
        non_budget_balance: '',
        account_balance: '',
        amount_due: '',
        budget_quantity_expected: {
            unit: '',
            quantity: 0
        },
        owner: {
            user_id: ''
        },
        alerts: {
            delivery_reminders: false,
            new_documents: false,
            schedule_reminders: false
        }
    },
    accountInquiry: {
        content: [],
        empty: false,
        totalPages: 0,
        totalElements: 0,
        numberOfElements: 0,
        pageNumber: 0
    },
    usersList: {
        empty: false,
        totalPages: 0,
        content: [],
        totalElements: 0,
        numberOfElements: 0,
        pageNumber: 0
    },
    accountFuelOrder: {
        content: [],
        empty: false,
        totalPages: 0,
        totalElements: 0,
        numberOfElements: 0,
        pageNumber: 0
    },
    accountApplication: {
        content: [],
        empty: false,
        totalPages: 0,
        totalElements: 0,
        numberOfElements: 0,
        pageNumber: 0
    },
    notificationConfigs: [],
    notificationConfigRoles: []
}

export const searchThunk = createAsyncThunk("account/searchThunk", async (arg: SearchTextArgs) => {
    try {
        const payload = {
            parameters: { find_by: arg.find_by }
        }
        const { data } = await searchUserAccountService(arg);
        return { data };
    } catch (error: any) {
        console.log("error--", error)
        throw new Error(error)
    }
})

export const getAccountDetailsThunk = createAsyncThunk("account/getAccountDetailsThunk", async (arg: string) => {
    try {
        const { data } = await getAccountDetailService(arg);
        return { data }
    } catch (error: any) {
        console.log("error--", error)
        throw new Error(error)
    }
})

export const getUsersListThunk = createAsyncThunk("userList/getUsersListThunk", async (args: ListArgs) => {
    try {
        const { data } = await getUsersListService(args);
        return { data }
    } catch (error: any) {
        console.log("error--", error);
        throw new Error(error);
    }
});

export const addWorkflowAdvanceThunk = createAsyncThunk("account/addWorkflowAdvanceThunk", async (arg: WorkflowAdvanceArgs) => {
    try {
        const { data } = await addWorkflowAdvanceService(arg);
        return { data }
    } catch (error: any) {
        console.log("error--", error)
        throw new Error(error)
    }
})

export const getAccountInquiryThunk = createAsyncThunk("account/getAccountInquiryThunk", async (arg: ListArgs) => {
    try {
        const { data } = await getAccountInquiriesService(arg);
        return { data }
    } catch (error: any) {
        console.log("error--", error)
        throw new Error(error)
    }
})

export const getAccountApplicationThunk = createAsyncThunk("account/getAccountApplicationThunk", async (arg: ListArgs) => {
    try {
        const { data } = await getAccountApplicationService(arg);
        return { data }
    } catch (error: any) {
        console.log("error--", error)
        throw new Error(error)
    }
})

export const getAccountFuelOrderThunk = createAsyncThunk("account/getAccountFuelOrderThunk", async (arg: ListArgs) => {
    try {
        const { data } = await getAccountFuelOrderService(arg);
        return { data }
    } catch (error: any) {
        console.log("error--", error)
        throw new Error(error)
    }
})

export const updateAccountStatusThunk = createAsyncThunk("account/updateAccountStatusThunk", async (arg: UpdateAccountArgs) => {
    try {
        const payload = {
            account_status: arg.account_status
        }
        const { data } = await updateAccountStatusService(payload, arg.accountId);
        return { data }
    } catch (error: any) {
        console.log("error--", error)
        throw new Error(error)
    }
})

export const updateAccountEmailThunk = createAsyncThunk("account/updateAccountEmailThunk", async (arg: UpdateEmailArgs) => {
    try {
        const payload = {
            email: arg.email,
            update_user: arg.update_user,
        }
        const { data } = await updateAccountStatusService(payload, arg.accountId);
        return { data }
    } catch (error: any) {
        console.log("error--", error)
        throw new Error(error)
    }
});

export const getNotificationConfigThunk = createAsyncThunk("account/getNotificationConfigThunk", async () => {
    try {
        const { data } = await getNotificationConfigService();
        return { data }
    } catch (error: any) {
        console.log("error--", error)
        throw new Error(error)
    }
})

export const getNotificationConfigRoleThunk = createAsyncThunk("account/getNotificationConfigRoleThunk", async (role: string) => {
    try {
        const { data } = await getNotificationConfigRoleService(role);
        return { data }
    } catch (error: any) {
        console.log("error--", error)
        throw new Error(error)
    }
})

export const updateNotificationConfigThunk = createAsyncThunk("account/updateNotificationConfigThunk", async (args: NotificationConfigUpdate[]) => {
    try {
        const { data } = await updateNotificationConfigService(args);
        return { data }
    } catch (error: any) {
        console.log("error--", error)
        throw new Error(error)
    }
})

export const registerUserThunk = createAsyncThunk("account/registerUserThunk", async (arg: SelfRegisterArgs, thunkAPI) => {
    try {
        const { data } = await registerUserService(arg)
        return { data }
    } catch (error: any) {
        console.log("error--", error.response.data);
        if (!error.response) {
            throw error
        }
        return thunkAPI.rejectWithValue(error.response.data?.error.error_code);
    }
});

export const accountSlice = createSlice({
    name: 'account',
    initialState,
    reducers: {
        errorReducer: (state) => {
            state.error = ""
        },
        successReducer: (state) => {
            state.success = ""
            state.successRegister = ""
        },
        clearUserState: (state) => {
            return initialState
        },
        clearAccountDetails: (state) => {
            state.accountDetails = {
                account_status: '',
                comment: null,
                account_id: '',
                balance_30_days: '',
                balance_60_days: '',
                balance_90_days: '',
                balance_over_90_days: '',
                billing_address: {
                    street1: '',
                    city: '',
                    state: '',
                    zipcode: '',
                    country: ''
                },
                budget_balance: '',
                budget_paid_YTD: '',
                budget_payment: '',
                display_name: '',
                email: '',
                first_name: '',
                home_phone: '',
                work_phone: '',
                cell_phone: '',
                is_budget_plan: false,
                is_cancelled: false,
                is_deleted: false,
                last_name: '',
                last_payment_amount: '',
                last_payment_date: '',
                non_budget_balance: '',
                account_balance: '',
                amount_due: '',
                budget_quantity_expected: {
                    unit: '',
                    quantity: 0
                },
                owner: {
                    user_id: ''
                },
                alerts: {
                    delivery_reminders: false,
                    new_documents: false,
                    schedule_reminders: false
                }
            }
        },
    },
    extraReducers(builder) {
        builder
            .addCase(getUsersListThunk.pending, (state, action) => {
                state.loading = true
            })
            .addCase(getUsersListThunk.fulfilled, (state, action) => {
                state.usersList = action.payload.data
                state.loading = false
                state.error = ""
            })
            .addCase(getUsersListThunk.rejected, (state, action) => {
                state.error = action.error.message!
                state.loading = false
            })
            .addCase(addWorkflowAdvanceThunk.pending, (state, action) => {
                state.loading = true
            })
            .addCase(addWorkflowAdvanceThunk.fulfilled, (state, action) => {
                state.success = "Added successfully"
                state.loading = false
                state.error = ""
            })
            .addCase(addWorkflowAdvanceThunk.rejected, (state, action) => {
                state.error = action.error.message!
                state.loading = false
            })
            .addCase(searchThunk.pending, (state, action) => {
                state.loading = true
            })
            .addCase(searchThunk.fulfilled, (state, action) => {
                state.accounts = action.payload.data
                state.loading = false
                state.error = ""
            })
            .addCase(searchThunk.rejected, (state, action) => {
                state.error = action.error.message!
                state.loading = false
            })
            .addCase(getAccountDetailsThunk.pending, (state, action) => {
                state.loading = true
            })
            .addCase(getAccountDetailsThunk.fulfilled, (state, action) => {
                state.accountDetails = action.payload.data
                state.loading = false
                state.error = ""
            })
            .addCase(getAccountDetailsThunk.rejected, (state, action) => {
                state.error = action.error.message!
                state.loading = false
            })
            .addCase(getAccountInquiryThunk.pending, (state, action) => {
                state.loading = true
            })
            .addCase(getAccountInquiryThunk.fulfilled, (state, action) => {
                state.accountInquiry = action.payload.data
                state.loading = false
                state.error = ""
            })
            .addCase(getAccountInquiryThunk.rejected, (state, action) => {
                state.error = action.error.message!
                state.loading = false
            })
            .addCase(getAccountFuelOrderThunk.pending, (state, action) => {
                state.loading = true
            })
            .addCase(getAccountFuelOrderThunk.fulfilled, (state, action) => {
                state.accountFuelOrder = action.payload.data
                state.loading = false
                state.error = ""
            })
            .addCase(getAccountFuelOrderThunk.rejected, (state, action) => {
                state.error = action.error.message!
                state.loading = false
            })
            .addCase(getAccountApplicationThunk.pending, (state, action) => {
                state.loading = true
            })
            .addCase(getAccountApplicationThunk.fulfilled, (state, action) => {
                state.accountApplication = action.payload.data
                state.loading = false
                state.error = ""
            })
            .addCase(getAccountApplicationThunk.rejected, (state, action) => {
                state.error = action.error.message!
                state.loading = false
            })
            .addCase(updateAccountStatusThunk.pending, (state, action) => {
                state.loading = true
            })
            .addCase(updateAccountStatusThunk.fulfilled, (state, action) => {
                state.success = "Account status updated successfully!"
                state.loading = false
                state.error = ""
            })
            .addCase(updateAccountStatusThunk.rejected, (state, action) => {
                state.error = action.error.message!
                state.loading = false
            })
            .addCase(updateAccountEmailThunk.pending, (state, action) => {
                state.loading = true
            })
            .addCase(updateAccountEmailThunk.fulfilled, (state, action) => {
                state.success = "Account Email updated successfully!"
                state.loading = false
                state.error = ""
            })
            .addCase(updateAccountEmailThunk.rejected, (state, action) => {
                state.error = action.error.message!
                state.loading = false
            })
            .addCase(getNotificationConfigThunk.pending, (state, action) => {
                state.loading = true
            })
            .addCase(getNotificationConfigThunk.fulfilled, (state, action) => {
                state.notificationConfigs = action.payload.data
                state.loading = false
                state.error = ""
            })
            .addCase(getNotificationConfigThunk.rejected, (state, action) => {
                state.error = action.error.message!
                state.loading = false
            })
            .addCase(getNotificationConfigRoleThunk.pending, (state, action) => {
                state.loading = true
            })
            .addCase(getNotificationConfigRoleThunk.fulfilled, (state, action) => {
                state.notificationConfigRoles = action.payload.data
                state.loading = false
                state.error = ""
            })
            .addCase(getNotificationConfigRoleThunk.rejected, (state, action) => {
                state.error = action.error.message!
                state.loading = false
            })
            .addCase(updateNotificationConfigThunk.pending, (state, action) => {
                state.loading = true
            })
            .addCase(updateNotificationConfigThunk.fulfilled, (state, action) => {
                state.success = "Notifications updated successfully!";
                state.notificationConfigRoles = action.payload.data
                state.loading = false
                state.error = ""
            })
            .addCase(updateNotificationConfigThunk.rejected, (state, action) => {
                state.error = action.error.message!
                state.loading = false
            })
            .addCase(registerUserThunk.pending, (state, action) => {
                state.loading = true
            })
            .addCase(registerUserThunk.fulfilled, (state, action) => {
                state.successRegister = "Thank you for registering for access.  Please check your email to verify your email address and complete the registration process. A message should arrive shortly, but please check your spam folder if you don't see it. Please call us for help if the email does not arrive."
                state.loading = false
                state.error = ""
            })
            .addCase(registerUserThunk.rejected, (state, action) => {
                state.error = state.error = action.payload as string || action.error.message || "";
                state.loading = false
            })
    },
})

export const accountActions = accountSlice.actions
export default accountSlice.reducer;